// ANY EDITS TO THIS PAGE SHOULD ALSO BE MADE TO ITS DEMO COUNTERPART, TO KEEP THE APP AND DEMO IN SYNC
import './ScoreAthletePage.css';

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { FirebaseError } from 'firebase/app';
import {
  collection,
  doc,
  getFirestore,
  onSnapshot,
} from 'firebase/firestore';
import {
  useNavigate,
  useParams,
} from 'react-router-dom';

import AthleteCardTop from '../components/common/AthleteCardTop/AthleteCardTop';
import AthleteQuickSelectTabBar
  from '../components/common/AthleteQuickSelectTabBar/AthleteQuickSelectTabBar';
import BackArrowIcon from '../components/common/BackButton/BackArrowIcon';
import Button from '../components/common/Button';
import ConfirmDialog from '../components/common/ConfirmDialog/ConfirmDialog';
import FullscreenPhotoViewer
  from '../components/common/FullscreenPhotoViewer/FullscreenPhotoViewer';
import PerCoachCommentsSection
  from '../components/common/PerCoachCommentsSection/PerCoachCommentsSection';
import IconButton from '../components/common/IconButton/IconButton';
import Input from '../components/common/Input';
import PageTopNav from '../components/common/PageTopNav/PageTopNav';
import PencilIcon from '../components/common/PencilIcon';
import PhotoUpload from '../components/common/PhotoUpload/PhotoUpload';
import SectionLauncherButton
  from '../components/common/SectionLauncherButton/SectionLauncherButton';
import SkillJudge from '../components/common/SkillJudge/SkillJudge';
import TrashIcon from '../components/common/TrashIcon';
import VideoUpload from '../components/common/VideoUpload/VideoUpload';
import { useScoringAuth } from '../contexts/ScoringAuthContext';
import { usePostHog } from '@posthog/react';
import { scoringApp } from '../firebase';
import {
  addGeneralComment,
  addSkillComment,
  athleteConverter,
  deleteAthlete,
  deleteGeneralComment,
  deleteSkillComment,
  editGeneralComment,
  editSkillComment,
  parsePerCoachComments,
  parseSkillFieldValue,
  parseTryoutMeta,
  setAthleteScoreField,
  updateAthlete,
} from '../services/athleteService';
import {
  setCoachTabbedAthletes,
  subscribeTryoutCoach,
  TryoutCoach,
} from '../services/coachService';
import {
  AthleteStatus,
  AthleteWithId,
  SkillValue,
} from '../types/athlete';
import {
  FormField,
  FormGroup,
  FormSchema,
  GENERAL_COMMENTS_FIELD_ID,
  GENERAL_COMMENTS_GROUP_ID,
  getSkillConfig,
} from '../types/formSchema';
import { isValidEmail } from '../utils/email';


type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready' };

interface TryoutMeta {
  name: string;
  athletePositions: string[];
  athleteStatuses: string[];
  formSchema: FormSchema;
}

const EMPTY_TRYOUT_META: TryoutMeta = {
  name: '',
  athletePositions: [],
  athleteStatuses: [],
  formSchema: { groups: [] },
};


const toFirestoreLoadMessage = (
  fallbackMessage: string,
  error: unknown,
): string => {
  if (error instanceof FirebaseError && error.code === 'permission-denied') {
    return 'You no longer have permission to access this tryout.';
  }
  return fallbackMessage;
};

const getAthleteCardViewerLabel = (athleteName: string): string => (
  `${athleteName} photo preview`
);

const MOBILE_CARD_COMPACT_MEDIA_QUERY = '(max-width: 768px)';
const DELETE_CONFIRM_DELAY_SECONDS = 5;

const ScoreAthletePage: React.FC = () => {
  const { tryoutId, athleteId } = useParams<{ tryoutId: string; athleteId: string }>();
  const { scoringUser } = useScoringAuth();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const db = getFirestore(scoringApp);

  const [loadState, setLoadState] = useState<LoadState>({ status: 'loading' });
  const [tryoutMeta, setTryoutMeta] = useState<TryoutMeta>(EMPTY_TRYOUT_META);
  const [athletes, setAthletes] = useState<AthleteWithId[]>([]);
  const [athletesLoaded, setAthletesLoaded] = useState(false);
  const [coach, setCoach] = useState<TryoutCoach | null>(null);
  const [activeGroupId, setActiveGroupId] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmSecondsRemaining, setDeleteConfirmSecondsRemaining] = useState(0);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState('');
  const [isDeletingAthlete, setIsDeletingAthlete] = useState(false);
  const [fieldSaveState, setFieldSaveState] = useState<{ fieldId: string; message: string } | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editNumber, setEditNumber] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editEmailError, setEditEmailError] = useState('');
  const [editPosition, setEditPosition] = useState('');
  const [editStatus, setEditStatus] = useState<AthleteStatus>(null);
  const [editImageUrl, setEditImageUrl] = useState('');
  const [editImageThumbnailUrl, setEditImageThumbnailUrl] = useState('');
  const [editImageStoragePathFull, setEditImageStoragePathFull] = useState('');
  const [editImageStoragePathThumb, setEditImageStoragePathThumb] = useState('');
  const [editPhotoUploading, setEditPhotoUploading] = useState(false);
  const [cardCompact, setCardCompact] = useState(false);
  const [cardViewerOpen, setCardViewerOpen] = useState(false);
  const scrollRegionRef = useRef<HTMLDivElement>(null);
  const stickyCardRef = useRef<HTMLElement>(null);

  /* eslint-disable react-hooks/exhaustive-deps -- omit athleteId: re-subscribing on tab switch breaks sticky/compact header UX */
  useEffect(() => {
    if (!tryoutId || !athleteId || !scoringUser) {
      setLoadState({ status: 'error', message: 'Invalid score page link.' });
      return;
    }
    setLoadState({ status: 'loading' });
    setAthletesLoaded(false);
    const unsubs: Array<() => void> = [];

    const tryoutRef = doc(db, 'tryouts', tryoutId);
    unsubs.push(onSnapshot(
      tryoutRef,
      (snap) => {
        if (!snap.exists()) {
          setLoadState({ status: 'error', message: 'Tryout not found.' });
          return;
        }
        const meta = parseTryoutMeta(snap.data());
        setTryoutMeta({
          name: meta.name,
          athletePositions: meta.athletePositions,
          athleteStatuses: meta.athleteStatuses,
          formSchema: meta.formSchema,
        });
        setLoadState({ status: 'ready' });
      },
      (error) => {
        setLoadState({
          status: 'error',
          message: toFirestoreLoadMessage('Failed to load tryout.', error),
        });
      },
    ));

    const athletesRef = collection(db, 'tryouts', tryoutId, 'athletes').withConverter(athleteConverter);
    unsubs.push(onSnapshot(
      athletesRef,
      (snap) => {
        setAthletes(snap.docs.map((d) => d.data()));
        setAthletesLoaded(true);
      },
      (error) => {
        setAthletesLoaded(true);
        setLoadState({
          status: 'error',
          message: toFirestoreLoadMessage('Failed to load athletes.', error),
        });
      },
    ));

    unsubs.push(subscribeTryoutCoach(
      db,
      tryoutId,
      scoringUser.uid,
      (nextCoach) => setCoach(nextCoach),
      () => setCoach(null),
    ));

    /**
     * Keep subscriptions stable across athlete tab switches.
     * Re-subscribing on every `athleteId` change causes transient state churn
     * that can break sticky/compact header behavior.
     */
    return () => {
      unsubs.forEach((fn) => fn());
    };
  }, [db, tryoutId, scoringUser]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const currentAthlete = useMemo(
    () => athletes.find((a) => a.id === athleteId) ?? null,
    [athletes, athleteId],
  );

  const quickAccessAthletes = useMemo(() => {
    const tabbedIds = coach?.tabbedAthletes;
    if (!tabbedIds || tabbedIds.length === 0) return [];
    const byId = new Map(athletes.map((a) => [a.id, a]));
    return tabbedIds
      .map((id) => byId.get(id))
      .filter((a): a is AthleteWithId => Boolean(a));
  }, [coach?.tabbedAthletes, athletes]);

  const groups = useMemo(
    () => [...tryoutMeta.formSchema.groups].sort((a, b) => a.order - b.order),
    [tryoutMeta.formSchema.groups],
  );

  useEffect(() => {
    if (!activeGroupId) return;
    if (!groups.some((g) => g.id === activeGroupId)) {
      setActiveGroupId('');
    }
  }, [groups, activeGroupId]);

  useEffect(() => {
    if (!currentAthlete) return;
    setEditName(currentAthlete.name);
    setEditNumber(currentAthlete.number);
    setEditEmail(currentAthlete.email ?? '');
    setEditEmailError('');
    setEditPosition(currentAthlete.position);
    setEditStatus(currentAthlete.returningstatus);
    setEditImageUrl(currentAthlete.imageUrl ?? '');
    setEditImageThumbnailUrl(currentAthlete.imageThumbnailUrl ?? '');
    setEditImageStoragePathFull('');
    setEditImageStoragePathThumb('');
  }, [currentAthlete]);

  useEffect(() => {
    const scrollRegion = scrollRegionRef.current;
    const stickyCard = stickyCardRef.current;
    if (!scrollRegion || !stickyCard) return;

    const updateStickyOffset = (): void => {
      scrollRegion.style.setProperty('--score-athlete-card-height', `${stickyCard.offsetHeight}px`);
    };

    updateStickyOffset();
    const observer = new ResizeObserver(() => updateStickyOffset());
    observer.observe(stickyCard);
    window.addEventListener('resize', updateStickyOffset);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateStickyOffset);
    };
  }, [currentAthlete?.id, activeGroupId]);

  useEffect(() => {
    const scrollRegion = scrollRegionRef.current;
    if (!scrollRegion) return;
    const mobileMq = window.matchMedia(MOBILE_CARD_COMPACT_MEDIA_QUERY);

    const COMPACT_ENTER_THRESHOLD_PX = 32;
    const COMPACT_EXIT_AT_TOP_PX = 2;
    const handleScroll = (): void => {
      if (!mobileMq.matches) {
        setCardCompact(false);
        return;
      }
      // Section launcher: keep the athlete card expanded (no compact-on-scroll).
      if (!activeGroupId) {
        setCardCompact(false);
        return;
      }
      const scrollTop = scrollRegion.scrollTop;
      setCardCompact((prev) => {
        if (!prev && scrollTop > COMPACT_ENTER_THRESHOLD_PX) return true;
        // Only expand when user is effectively back at the top.
        // This avoids oscillation from layout shifts while sticky elements resize.
        if (prev && scrollTop <= COMPACT_EXIT_AT_TOP_PX) return false;
        return prev;
      });
    };
    const handleViewportChange = (): void => {
      if (!mobileMq.matches) {
        setCardCompact(false);
      } else {
        handleScroll();
      }
    };

    // Re-evaluate compact mode for the current scroll position when athlete changes.
    // Intentionally do not force reset to expanded on athlete tab switches.
    handleScroll();
    scrollRegion.addEventListener('scroll', handleScroll, { passive: true });
    mobileMq.addEventListener('change', handleViewportChange);
    return () => {
      scrollRegion.removeEventListener('scroll', handleScroll);
      mobileMq.removeEventListener('change', handleViewportChange);
    };
  }, [currentAthlete?.id, activeGroupId]);

  useEffect(() => {
    setCardViewerOpen(false);
  }, [currentAthlete?.id]);

  useEffect(() => {
    if (!showDeleteDialog) {
      setDeleteConfirmSecondsRemaining(0);
      return;
    }

    setDeleteConfirmSecondsRemaining(DELETE_CONFIRM_DELAY_SECONDS);
    const intervalId = window.setInterval(() => {
      setDeleteConfirmSecondsRemaining((prev) => {
        if (prev <= 1) {
          window.clearInterval(intervalId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [showDeleteDialog]);

  const activeGroup = groups.find((g) => g.id === activeGroupId) ?? null;
  /** Compact scroll behavior only while a scoring section is open (not on the section launcher). */
  const cardCompactForUi = Boolean(activeGroupId) && cardCompact;
  const inQuickAccess = !!coach?.tabbedAthletes.includes(athleteId ?? '');
  const hasQuickAccessHotbar = quickAccessAthletes.length > 0;
  const isDeleteConfirmLocked = deleteConfirmSecondsRemaining > 0;
  const deleteConfirmLabel = isDeleteConfirmLocked
    ? `Delete (${deleteConfirmSecondsRemaining})`
    : 'Delete';

  if (loadState.status === 'loading' || !athletesLoaded) {
    return (
      <></>
    );
  }
  if (isDeletingAthlete) {
    return (
      <></>
    );
  }
  if (loadState.status === 'error' || !tryoutId || !athleteId || !scoringUser) {
    return (
      <div className="score-athlete-shell score-athlete-shell--center">
        <p>{loadState.status === 'error' ? loadState.message : 'Invalid page.'}</p>
        <Button onClick={() => navigate(`/tryout/${tryoutId}/athletes`)}>Back</Button>
      </div>
    );
  }
  if (!currentAthlete) {
    return (
      <div className="score-athlete-shell score-athlete-shell--center">
        <p>Athlete not found.</p>
        <Button onClick={() => navigate(`/tryout/${tryoutId}/athletes`)}>Back to Athletes</Button>
      </div>
    );
  }
  const cardImageSrc = currentAthlete.imageUrl || currentAthlete.imageThumbnailUrl || null;

  const toggleQuickAccess = async (): Promise<void> => {
    if (!coach) return;
    const current = coach.tabbedAthletes;
    const next = inQuickAccess
      ? current.filter((id) => id !== currentAthlete.id)
      : [...current, currentAthlete.id];
    try {
      await setCoachTabbedAthletes(db, tryoutId, scoringUser.uid, next);
    } catch (error: unknown) {
      console.error('Failed to toggle quick access athlete:', error);
    }
  };

  const handleDeleteAthlete = async (): Promise<void> => {
    setIsDeletingAthlete(true);
    try {
      await deleteAthlete(db, tryoutId, currentAthlete.id);
      posthog?.capture('athlete_deleted', { tryout_id: tryoutId });
      setDeleteErrorMessage('');
      setShowDeleteDialog(false);
      navigate(`/tryout/${tryoutId}/athletes`);
    } catch (error: unknown) {
      setIsDeletingAthlete(false);
      if (error instanceof FirebaseError && error.code === 'permission-denied') {
        setDeleteErrorMessage('You do not have permission to delete athletes in this tryout.');
        return;
      }
      posthog?.captureException(error);
      setDeleteErrorMessage('Failed to delete athlete. Please try again.');
    }
  };

  const handleSaveEdit = async (): Promise<void> => {
    const trimmedEmail = editEmail.trim();
    if (trimmedEmail && !isValidEmail(trimmedEmail)) {
      setEditEmailError('Enter a valid athlete email.');
      return;
    }
    setEditEmailError('');
    await updateAthlete(db, tryoutId, currentAthlete.id, {
      name: editName.trim(),
      number: editNumber.trim(),
      email: trimmedEmail,
      position: editPosition,
      returningstatus: editStatus,
      imageUrl: editImageUrl.trim(),
      imageThumbnailUrl: editImageThumbnailUrl.trim(),
    });
    posthog?.capture('athlete_edited', { tryout_id: tryoutId });
    setEditOpen(false);
  };

  const resetEditForm = (): void => {
    if (!currentAthlete) return;
    setEditName(currentAthlete.name);
    setEditNumber(currentAthlete.number);
    setEditEmail(currentAthlete.email ?? '');
    setEditEmailError('');
    setEditPosition(currentAthlete.position);
    setEditStatus(currentAthlete.returningstatus);
    setEditImageUrl(currentAthlete.imageUrl ?? '');
    setEditImageThumbnailUrl(currentAthlete.imageThumbnailUrl ?? '');
    setEditImageStoragePathFull('');
    setEditImageStoragePathThumb('');
    setEditPhotoUploading(false);
  };

  const handleCloseEditModal = (): void => {
    resetEditForm();
    setEditOpen(false);
  };

  const handleScoreChange = async (field: FormField, value: SkillValue): Promise<void> => {
    try {
      await setAthleteScoreField(db, tryoutId, currentAthlete.id, field.id, value);
    } catch {
      // no-op: keep current UX without transient status text for non-photo fields
    }
  };

  const handleVideoScoreChange = async (field: FormField, value: SkillValue): Promise<void> => {
    try {
      await setAthleteScoreField(db, tryoutId, currentAthlete.id, field.id, value);
      window.setTimeout(() => {
        setFieldSaveState((prev) => (prev?.fieldId === field.id ? null : prev));
      }, 900);
    } catch {
      setFieldSaveState({ fieldId: field.id, message: 'Failed to save' });
    }
  };

  const handlePhotoScoreChange = async (field: FormField, value: SkillValue): Promise<void> => {
    try {
      await setAthleteScoreField(db, tryoutId, currentAthlete.id, field.id, value);
      window.setTimeout(() => {
        setFieldSaveState((prev) => (prev?.fieldId === field.id ? null : prev));
      }, 900);
    } catch {
      setFieldSaveState({ fieldId: field.id, message: 'Failed to save' });
    }
  };

  const renderSkillField = (field: FormField): React.ReactNode => {
    if (field.type === 'heading') {
      return <h3 className="score-athlete-heading-field">{field.label}</h3>;
    }
    const cfg = getSkillConfig(field);
    const raw = currentAthlete.scores[field.id];
    const fieldValue = parseSkillFieldValue(raw);
    const photoValue = fieldValue.photo ?? null;
    const videoValue = fieldValue.video ?? null;
    const ratingValue = fieldValue.rating ?? 0;

    return (
      <div className="score-athlete-skill-row">
        {cfg.rating ? (
          <SkillJudge
            title={field.label}
            value={ratingValue}
            maxRating={cfg.maxRating}
            onChange={(val) => {
              void handleScoreChange(field, { ...fieldValue, rating: val });
            }}
          />
        ) : (
          <h3 className="score-athlete-skill-title">{field.label}</h3>
        )}
        {cfg.comment ? (
          <PerCoachCommentsSection
            comments={fieldValue.comments ?? []}
            currentCoachUid={scoringUser.uid}
            currentCoachName={coach?.name ?? scoringUser.displayName ?? 'Coach'}
            onAddComment={async (text) => {
              const coachName = coach?.name ?? scoringUser.displayName ?? 'Coach';
              await addSkillComment(db, tryoutId, currentAthlete.id, field.id, {
                text,
                coachName,
                coachUid: scoringUser.uid,
              });
            }}
            onEditComment={async (commentId, newText) => {
              await editSkillComment(db, tryoutId, currentAthlete.id, field.id, commentId, newText);
            }}
            onDeleteComment={async (commentId) => {
              await deleteSkillComment(db, tryoutId, currentAthlete.id, field.id, commentId);
            }}
          />
        ) : null}
        {cfg.photo ? (
          <div className="score-athlete-photo-field">
            <PhotoUpload
              tryoutId={tryoutId}
              coachUid={scoringUser.uid}
              uploadVariant="score"
              compactEmpty
              label={`${field.label} photo`}
              imageUrl={photoValue?.imageUrl}
              storagePathFull={photoValue?.storagePathFull}
              storagePathThumb={photoValue?.storagePathThumb}
              statusText={fieldSaveState?.fieldId === field.id ? fieldSaveState.message : undefined}
              onImageUpload={(payload) => {
                void handlePhotoScoreChange(field, { ...fieldValue, photo: payload });
              }}
            />
          </div>
        ) : null}
        {cfg.video ? (
          <div className="score-athlete-photo-field">
            <VideoUpload
              tryoutId={tryoutId}
              coachUid={scoringUser.uid}
              db={db}
              athleteId={currentAthlete.id}
              athleteName={currentAthlete.name}
              fieldId={field.id}
              compactEmpty
              label={`${field.label} video`}
              videoUid={videoValue?.videoUid}
              statusText={fieldSaveState?.fieldId === field.id ? fieldSaveState.message : undefined}
              onVideoChange={(val) => {
                if (val) {
                  // Upload-success path goes through the VideoUploadProvider,
                  // which writes Firestore directly. This branch only fires
                  // for code paths that still call onVideoChange manually.
                  void handleVideoScoreChange(field, { ...fieldValue, video: val });
                } else {
                  // Omit the video key entirely — Firestore rejects `undefined` values
                  const { video: _removed, ...rest } = fieldValue;
                  void handleVideoScoreChange(field, rest);
                }
              }}
            />
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="score-athlete-shell">
      <PageTopNav
        onBack={() => navigate(`/tryout/${tryoutId}/athletes`)}
        backAriaLabel="Back to athlete list"
        title={tryoutMeta.name}
      />

      <div ref={scrollRegionRef} className="score-athlete-scroll-region">
        <section
          ref={stickyCardRef}
          className={`score-athlete-card${cardCompactForUi ? ' score-athlete-card--compact' : ''}`}
        >
          <AthleteCardTop
            name={currentAthlete.name}
            number={currentAthlete.number}
            position={currentAthlete.position}
            returningstatus={currentAthlete.returningstatus}
            imageSrc={cardImageSrc}
            compact={cardCompactForUi}
            onPhotoClick={() => setCardViewerOpen(true)}
            rightSlot={(
              <div className="score-athlete-card-actions">
                <button
                  type="button"
                  className={`score-athlete-quick-access-btn${inQuickAccess ? ' score-athlete-quick-access-btn--active' : ''}`}
                  onClick={() => void toggleQuickAccess()}
                  aria-label={inQuickAccess ? 'Remove from quick access' : 'Add to quick access'}
                >
                  {inQuickAccess ? '★' : '☆'}
                </button>
                <button
                  type="button"
                  className="score-athlete-edit-btn"
                  onClick={() => setEditOpen(true)}
                  aria-label="Edit athlete"
                >
                  <PencilIcon />
                </button>
              </div>
            )}
          />
        </section>

        <main
          className={`score-athlete-content${hasQuickAccessHotbar ? ' score-athlete-content--with-hotbar' : ''}`}
        >
          {activeGroup ? (
            <section className="score-athlete-fields">
              <button
                type="button"
                className="score-athlete-section-header"
                onClick={() => setActiveGroupId('')}
                aria-label="Back to sections"
              >
                <span className="score-athlete-section-header-back-icon" aria-hidden="true">
                  <BackArrowIcon />
                </span>
                <h3 className="score-athlete-section-title">{activeGroup.label}</h3>
              </button>
              {activeGroup.id === GENERAL_COMMENTS_GROUP_ID ? (
                <PerCoachCommentsSection
                  comments={parsePerCoachComments(currentAthlete.scores[GENERAL_COMMENTS_FIELD_ID])}
                  currentCoachUid={scoringUser.uid}
                  currentCoachName={coach?.name ?? scoringUser.displayName ?? 'Coach'}
                  onAddComment={async (text) => {
                    const coachName = coach?.name ?? scoringUser.displayName ?? 'Coach';
                    await addGeneralComment(db, tryoutId, currentAthlete.id, {
                      text,
                      coachName,
                      coachUid: scoringUser.uid,
                    });
                  }}
                  onEditComment={async (commentId, newText) => {
                    await editGeneralComment(db, tryoutId, currentAthlete.id, commentId, newText);
                  }}
                  onDeleteComment={async (commentId) => {
                    await deleteGeneralComment(db, tryoutId, currentAthlete.id, commentId);
                  }}
                />
              ) : (
                <div className="score-athlete-fields-list">
                  {[...activeGroup.fields]
                    .sort((a, b) => a.order - b.order)
                    .map((field) => (
                      <div key={field.id}>{renderSkillField(field)}</div>
                    ))}
                </div>
              )}
            </section>
          ) : (
            <section className="score-athlete-section-launcher">
              {groups.length === 0 ? (
                <p>No scoring sections configured for this tryout.</p>
              ) : (
                groups.map((group: FormGroup) => {
                  if (group.id === GENERAL_COMMENTS_GROUP_ID) {
                    const commentCount = parsePerCoachComments(
                      currentAthlete.scores[GENERAL_COMMENTS_FIELD_ID],
                    ).length;
                    return (
                      <SectionLauncherButton
                        key={group.id}
                        label={group.label}
                        scoredCount={commentCount}
                        scorableCount={commentCount}
                        isGeneralComments
                        onClick={() => setActiveGroupId(group.id)}
                      />
                    );
                  }
                  const scorableFields = group.fields.filter((f) => f.type !== 'heading');
                  const scoredFields = scorableFields.filter((f) => {
                    const val = currentAthlete.scores[f.id];
                    if (!val) return false;
                    const parsed = parseSkillFieldValue(val);
                    return (
                      (typeof parsed.rating === 'number' && parsed.rating > 0)
                      || (parsed.comments?.length ?? 0) > 0
                      || parsed.photo !== undefined
                      || parsed.video !== undefined
                    );
                  });
                  return (
                    <SectionLauncherButton
                      key={group.id}
                      label={group.label}
                      scoredCount={scoredFields.length}
                      scorableCount={scorableFields.length}
                      onClick={() => setActiveGroupId(group.id)}
                    />
                  );
                })
              )}
            </section>
          )}

        </main>
      </div>

      {hasQuickAccessHotbar ? (
        <div className="athlete-quick-select-hotbar">
          <AthleteQuickSelectTabBar
            athletes={quickAccessAthletes}
            currentAthleteId={currentAthlete.id}
            onAthleteSelect={(id) => navigate(`/tryout/${tryoutId}/athletes/${id}`)}
          />
        </div>
      ) : null}

      <ConfirmDialog
        open={showDeleteDialog}
        title="Delete athlete?"
        message={`${deleteErrorMessage || `Delete ${currentAthlete.name}? This cannot be undone.`}`}
        confirmLabel={deleteConfirmLabel}
        confirmDisabled={isDeleteConfirmLocked}
        onCancel={() => {
          setDeleteErrorMessage('');
          setShowDeleteDialog(false);
        }}
        onConfirm={() => {
          if (isDeleteConfirmLocked) return;
          void handleDeleteAthlete();
        }}
      />
      {editOpen ? (
        <div
          className="score-athlete-edit-modal-overlay"
          role="presentation"
          onClick={handleCloseEditModal}
        >
          <div
            className="score-athlete-edit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="score-athlete-edit-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="score-athlete-edit-modal-header">
              <h3 id="score-athlete-edit-modal-title" className="score-athlete-edit-modal-title">
                Edit Athlete
              </h3>
              <IconButton
                className="score-athlete-edit-modal-delete-btn"
                aria-label="Delete athlete"
                onClick={() => {
                  setDeleteErrorMessage('');
                  setShowDeleteDialog(true);
                }}
              >
                <TrashIcon />
              </IconButton>
            </div>
            <div className="score-athlete-edit">
              <Input
                label="Name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
              <Input
                label="Number"
                value={editNumber}
                onChange={(e) => setEditNumber(e.target.value)}
              />
              <Input
                type="email"
                label="Athlete Email"
                value={editEmail}
                error={editEmailError}
                onChange={(e) => {
                  setEditEmail(e.target.value);
                  setEditEmailError('');
                }}
                inputMode="email"
                autoCapitalize="off"
                autoComplete="email"
              />
              <PhotoUpload
                tryoutId={tryoutId}
                coachUid={scoringUser.uid}
                label="Athlete photo"
                imageUrl={editImageUrl}
                storagePathFull={editImageStoragePathFull}
                storagePathThumb={editImageStoragePathThumb}
                onImageUpload={(payload) => {
                  setEditImageUrl(payload.imageUrl);
                  setEditImageThumbnailUrl(payload.imageThumbnailUrl);
                  setEditImageStoragePathFull(payload.storagePathFull);
                  setEditImageStoragePathThumb(payload.storagePathThumb);
                }}
                onUploadingChange={setEditPhotoUploading}
              />
              <label className="score-athlete-select-label">
                Position
                <select
                  value={editPosition}
                  onChange={(e) => setEditPosition(e.target.value)}
                  className="score-athlete-select"
                >
                  {(tryoutMeta.athletePositions.length > 0 ? tryoutMeta.athletePositions : ['Unknown'])
                    .map((position) => (
                      <option key={position} value={position}>{position}</option>
                    ))}
                </select>
              </label>
              <label className="score-athlete-select-label">
                Returning Status
                <select
                  value={editStatus ?? ''}
                  onChange={(e) => setEditStatus(e.target.value || null)}
                  className="score-athlete-select"
                >
                  <option value="">None</option>
                  {tryoutMeta.athleteStatuses.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </label>
              <div className="score-athlete-edit-modal-actions">
                <Button variant="secondary" onClick={handleCloseEditModal}>
                  Cancel
                </Button>
                <Button onClick={() => void handleSaveEdit()} disabled={editPhotoUploading}>
                  Save Athlete
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {cardImageSrc ? (
        <FullscreenPhotoViewer
          open={cardViewerOpen}
          src={cardImageSrc}
          alt={currentAthlete.name}
          viewerLabel={getAthleteCardViewerLabel(currentAthlete.name)}
          onClose={() => setCardViewerOpen(false)}
        />
      ) : null}
    </div>
  );
};

export default ScoreAthletePage;
